import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  RoundButton as RoundedButton,
  ArrowL12 as IconChevronLeft12,
  ArrowR12 as IconChevronRight12,
} from '@cian/ui-kit';

import { debounce, useIsMounted } from '../../utils';
import { TagScrolling } from './TagScrolling';
import { ITagScrollingParams } from './types';
import * as s from './TagScrollingWithControls.css';

/** Панель прокрутки тэгов с контролами */
export const TagScrollingWithControls = ({ items, ...otherProps }: ITagScrollingParams) => {
  const tagsWrapperRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canSeeControls, setCanSeeControls] = useState(false);
  const isMounted = useIsMounted();

  /** Скроллит контейнер тэгов */
  const scroll = useCallback((toPrev?: boolean) => {
    const ref = tagsWrapperRef?.current;

    if (!ref) {
      return;
    }

    ref.scrollBy({ left: (toPrev ? -1 : 1) * 200, behavior: 'smooth' });
  }, []);

  /** Проверяет состояние контролов */
  const checkForScrollPosition = useCallback(() => {
    const ref = tagsWrapperRef?.current;

    // Проверяем isMounted, т.к. данная функция может быть вызвана через debounce
    if (!ref || !isMounted()) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = ref;

    /** Показывает/прячет контролы (если все тэги умещаются в строке -> прячет контролы) */
    setCanSeeControls(scrollWidth > clientWidth);
    /** Дизэйблит контрол, если достигнут левый предел прокрутки */
    setCanScrollPrev(scrollLeft > 0);
    /** Дизэйблит контрол, если достигнут правый предел прокрутки */
    setCanScrollNext(scrollLeft <= scrollWidth - clientWidth);
  }, [isMounted]);

  /** Проверяет состояние контролов с задержкой */
  const checkForScrollPositionDebounced = debounce(checkForScrollPosition, 100);

  /** Включает прослушку скроллинга и дизэйблит контролы */
  useEffect(() => {
    const ref = tagsWrapperRef?.current;

    if (!ref) {
      return;
    }

    checkForScrollPosition();
    ref.addEventListener('scroll', checkForScrollPositionDebounced);

    return () => {
      ref.removeEventListener('scroll', checkForScrollPositionDebounced);
    };
  }, [checkForScrollPosition, checkForScrollPositionDebounced]);

  if (!items?.length) {
    return null;
  }

  return (
    <div className={`${s['wrapper']}`}>
      {canSeeControls && (
        <div className={`${s['control-wrapper']} ${s['prev']}`}>
          <RoundedButton
            icon={<IconChevronLeft12 />}
            size="XS"
            theme="fill_light"
            title="Показать предыдущие"
            disabled={!canScrollPrev}
            onClick={() => scroll(true)}
          />
        </div>
      )}

      <TagScrolling items={items} ref={tagsWrapperRef} {...otherProps} />

      {canSeeControls && (
        <div className={`${s['control-wrapper']} ${s['next']}`}>
          <RoundedButton
            icon={<IconChevronRight12 />}
            size="XS"
            theme="fill_light"
            title="Показать следующие"
            disabled={!canScrollNext}
            onClick={() => scroll()}
          />
        </div>
      )}
    </div>
  );
};
